import React, { useState, useEffect, useRef } from "react";
import { Trash2, CalendarDays } from "lucide-react";
import DatePicker from "./DatePicker";
import StatusDropdown from "./StatusDropdown";
import "../../../../styles/kanbans/activities.css";

type Activity = {
  id: number;
  uid: string;
  title: string;
  company: string;
  position: string;
  category: string;
  status: string;
  timeAgo: string;
  note?: string;
  startDate?: string;
  endDate?: string;
  type?: string;
  is_completed?: boolean;
  job_uid?: string | null;
  board_uid?: string;
};

type Props = {
  activities: Activity[];
  onDeleteActivity?: (activityId: number) => void;
  onUpdateActivity?: (activityUid: string, updates: {
    title?: string;
    type?: string;
    description?: string | null;
    is_completed?: boolean;
    start_date?: string | null;
    end_date?: string | null;
  }) => Promise<void>;
  onJobClick?: (jobId: string, boardId: string, jobTitle: string, companyName: string) => void;
};

export default function ActivitiesList({ activities, onDeleteActivity, onUpdateActivity, onJobClick }: Props) {
  const [expanded, setExpanded] = useState<number | null>(null);
  const [titles, setTitles] = useState<{ [key: number]: string }>({});
  const [notes, setNotes] = useState<{ [key: number]: string }>({});
  const [showStartCalendar, setShowStartCalendar] = useState<number | null>(null);
  const [showEndCalendar, setShowEndCalendar] = useState<number | null>(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState<number | null>(null);
  const [startDates, setStartDates] = useState<{ [key: number]: Date | null }>({});
  const [endDates, setEndDates] = useState<{ [key: number]: Date | null }>({});
  const [statuses, setStatuses] = useState<{ [key: number]: string }>({});
  const [checked, setChecked] = useState<{ [key: number]: boolean }>({});
  const [saveStatus, setSaveStatus] = useState<{ [key: number]: 'idle' | 'saving' | 'saved' | 'error' }>({});
  const debounceTimers = useRef<{ [key: number]: NodeJS.Timeout }>({});

  useEffect(() => {
    const newStartDates: { [key: number]: Date | null } = {};
    const newEndDates: { [key: number]: Date | null } = {};
    const newChecked: { [key: number]: boolean } = {};
    const newStatuses: { [key: number]: string } = {};
    
    activities.forEach(activity => {
      if (activity.startDate) {
        const date = typeof activity.startDate === 'string' 
          ? new Date(activity.startDate) 
          : activity.startDate;
        if (!isNaN(date.getTime())) {
          newStartDates[activity.id] = date;
        }
      }
      if (activity.endDate) {
        const date = typeof activity.endDate === 'string' 
          ? new Date(activity.endDate) 
          : activity.endDate;
        if (!isNaN(date.getTime())) {
          newEndDates[activity.id] = date;
        }
      }
      if (activity.is_completed !== undefined) {
        newChecked[activity.id] = activity.is_completed;
      }
      if (activity.type) {
        newStatuses[activity.id] = activity.type;
      }
    });
    
    setStartDates(prev => ({ ...prev, ...newStartDates }));
    setEndDates(prev => ({ ...prev, ...newEndDates }));
    setChecked(prev => ({ ...prev, ...newChecked }));
    setStatuses(prev => ({ ...prev, ...newStatuses }));
  }, [activities]);

  const toggleExpand = (id: number) => {
    setExpanded(expanded === id ? null : id);
  };

  const handleTitleChange = (id: number, value: string) => {
    setTitles({ ...titles, [id]: value });
    debounceSave(id, 'title', value);
  };

  const handleNoteChange = (id: number, value: string) => {
    setNotes({ ...notes, [id]: value });
    debounceSave(id, 'description', value);
  };

  const formatDate = (date: Date | null | undefined): string => {
    if (!date) return "Select date";
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  };

  const handleStartDateSelect = (activityId: number, date: Date) => {
    setStartDates({ ...startDates, [activityId]: date });
    setShowStartCalendar(null);
    
    const currentEndDate = endDates[activityId];
    if (currentEndDate && currentEndDate < date) {
      setEndDates({ ...endDates, [activityId]: null });
      debounceSave(activityId, 'end_date', null);
    }
    
    debounceSave(activityId, 'start_date', date.toISOString());
  };

  const handleEndDateSelect = (activityId: number, date: Date) => {
    setEndDates({ ...endDates, [activityId]: date });
    setShowEndCalendar(null);
    
    const currentStartDate = startDates[activityId];
    if (currentStartDate && currentStartDate > date) {
      setStartDates({ ...startDates, [activityId]: null });
      debounceSave(activityId, 'start_date', null);
    }
    
    debounceSave(activityId, 'end_date', date.toISOString());
  };


  const getMinEndDate = (activityId: number): Date | undefined => {
    return startDates[activityId] || undefined;
  };

  const getMaxStartDate = (activityId: number): Date | undefined => {
    return endDates[activityId] || undefined;
  };

  const handleStatusChange = (activityId: number, status: string) => {
    setStatuses({ ...statuses, [activityId]: status });
    debounceSave(activityId, 'type', status);
  };

  const handleCheckboxChange = (activityId: number, isChecked: boolean) => {
    setChecked({ ...checked, [activityId]: isChecked });
    debounceSave(activityId, 'is_completed', isChecked);
  };

  const debounceSave = (activityId: number, field: string, value: any) => {
    if (!onUpdateActivity) return;

    const activity = activities.find(a => a.id === activityId);
    if (!activity || !activity.uid) return;

    if (debounceTimers.current[activityId]) {
      clearTimeout(debounceTimers.current[activityId]);
    }

    setSaveStatus(prev => ({ ...prev, [activityId]: 'saving' }));

    debounceTimers.current[activityId] = setTimeout(async () => {
      try {
        const currentTitle = titles[activityId] !== undefined ? titles[activityId] : activity.title;
        const currentNote = notes[activityId] !== undefined ? notes[activityId] : (activity.note || '');
        
        let currentStartDate: string | null;
        if (field === 'start_date') {
          currentStartDate = value;
        } else {
          currentStartDate = startDates[activityId] !== undefined 
            ? (startDates[activityId] ? startDates[activityId]!.toISOString() : null)
            : (activity.startDate || null);
        }
        
        let currentEndDate: string | null;
        if (field === 'end_date') {
          currentEndDate = value;
        } else {
          currentEndDate = endDates[activityId] !== undefined
            ? (endDates[activityId] ? endDates[activityId]!.toISOString() : null)
            : (activity.endDate || null);
        }
        
        const currentStatus = statuses[activityId] !== undefined ? statuses[activityId] : (activity.type || activity.status);
        
        let currentIsCompleted: boolean;
        if (field === 'is_completed') {
          currentIsCompleted = value;
        } else {
          currentIsCompleted = checked[activityId] !== undefined 
            ? checked[activityId] 
            : (activity.is_completed || false);
        }
        
        let currentType: string;
        if (field === 'type') {
          currentType = value;
        } else {
          currentType = currentStatus || activity.type || mapCategoryToType(activity.category) || 'Other';
        }

        const updates: any = {
          title: currentTitle,
          type: currentType,
          description: currentNote || null,
          is_completed: currentIsCompleted,
          start_date: currentStartDate,
          end_date: currentEndDate,
        };

        await onUpdateActivity(activity.uid, updates);
        
        setSaveStatus(prev => ({ ...prev, [activityId]: 'saved' }));
        
        setTimeout(() => {
          setSaveStatus(prev => {
            const newStatus = { ...prev };
            if (newStatus[activityId] === 'saved') {
              delete newStatus[activityId];
            }
            return newStatus;
          });
        }, 2000);
      } catch (error) {

        setSaveStatus(prev => ({ ...prev, [activityId]: 'error' }));
        
        setTimeout(() => {
          setSaveStatus(prev => {
            const newStatus = { ...prev };
            if (newStatus[activityId] === 'error') {
              delete newStatus[activityId];
            }
            return newStatus;
          });
        }, 3000);
      }
    }, 1500);
  };

  const mapCategoryToType = (category: string): string => {
    const categoryLower = category.toLowerCase();
    if (categoryLower.includes("interview")) {
      return "On Site Interview";
    }
    if (categoryLower.includes("application")) {
      return "Apply";
    }
    if (categoryLower.includes("networking")) {
      return "Networking Event";
    }
    return "Other";
  };

  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  const hasActiveDropdown = (activityId: number): boolean => {
    return showStartCalendar === activityId || 
           showEndCalendar === activityId || 
           showStatusDropdown === activityId;
  };

  return (
    <section className="activities-list">
      {activities.map((a) => (
        <div 
          key={a.id} 
          className={`activity-container ${hasActiveDropdown(a.id) ? 'has-active-dropdown' : ''}`}
        >
          <div
            className={`activity-row ${expanded === a.id ? "expanded" : ""}`}
            onClick={() => toggleExpand(a.id)}
          >
            <div className="activity-col left">
              <input 
                type="checkbox" 
                checked={checked[a.id] !== undefined ? checked[a.id] : (a.is_completed || false)}
                onChange={(e) => {
                  e.stopPropagation();
                  handleCheckboxChange(a.id, e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()} 
              />
              {expanded === a.id ? (
                <input
                  type="text"
                  className="activity-title-editable"
                  value={titles[a.id] !== undefined ? titles[a.id] : a.title}
                  onChange={(e) => handleTitleChange(a.id, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Activity title"
                />
              ) : (
                <span className="activity-title">{titles[a.id] !== undefined ? titles[a.id] : a.title}</span>
              )}
            </div>

            <div className="activity-col center">
              <span className="activity-company">{a.company}</span>
            </div>

            <div className="activity-col center">
              {a.job_uid && a.board_uid ? (
                <span 
                  className="activity-position"
                  style={{ 
                    cursor: 'pointer', 
                    color: '#3b82f6',
                    textDecoration: 'underline'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onJobClick?.(a.job_uid!, a.board_uid!, a.position, a.company);
                  }}
                >
                  {a.position}
                </span>
              ) : (
                <span className="activity-position">{a.position}</span>
              )}
            </div>

            <div className="activity-col center">
              <span className="activity-status success">
                {statuses[a.id] !== undefined ? statuses[a.id] : (a.type || a.status)}
              </span>
            </div>

            <div className="activity-col center" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className="activity-time">{a.timeAgo}</span>
              {saveStatus[a.id] === 'saving' && (
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Saving...</span>
              )}
              {saveStatus[a.id] === 'saved' && (
                <span style={{ fontSize: '12px', color: '#10b981' }}>✓ Saved</span>
              )}
              {saveStatus[a.id] === 'error' && (
                <span style={{ fontSize: '12px', color: '#ef4444' }}>✗ Error</span>
              )}
            </div>
          </div>

          {expanded === a.id && (
            <div className="activity-expanded">
              <textarea
                className="activity-note-input"
                placeholder="Add a note..."
                value={notes[a.id] || a.note || ""}
                onChange={(e) => handleNoteChange(a.id, e.target.value)}
                rows={3}
              />

              <div className="activity-expanded-footer">
                <div className="activity-expanded-controls">
                  <div className="date-picker-wrapper">
                    <button
                      className="btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowStartCalendar(showStartCalendar === a.id ? null : a.id);
                        setShowEndCalendar(null);
                      }}
                      type="button"
                    >
                      <CalendarDays size={14} style={{ marginRight: 6 }} />
                      {formatDate(startDates[a.id] || (a.startDate ? (typeof a.startDate === 'string' ? new Date(a.startDate) : a.startDate) : null))}
                    </button>
                    {showStartCalendar === a.id && (
                      <DatePicker
                        selectedDate={startDates[a.id] || (a.startDate ? (typeof a.startDate === 'string' ? new Date(a.startDate) : a.startDate) : null)}
                        onSelect={(date) => handleStartDateSelect(a.id, date)}
                        onClose={() => setShowStartCalendar(null)}
                        maxDate={getMaxStartDate(a.id)}
                      />
                    )}
                  </div>

                  <div className="date-picker-wrapper">
                    <button
                      className="btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowEndCalendar(showEndCalendar === a.id ? null : a.id);
                        setShowStartCalendar(null);
                      }}
                      type="button"
                    >
                      <CalendarDays size={14} style={{ marginRight: 6 }} />
                      {formatDate(endDates[a.id] || (a.endDate ? (typeof a.endDate === 'string' ? new Date(a.endDate) : a.endDate) : null))}
                    </button>
                    {showEndCalendar === a.id && (
                      <DatePicker
                        selectedDate={endDates[a.id] || (a.endDate ? (typeof a.endDate === 'string' ? new Date(a.endDate) : a.endDate) : null)}
                        onSelect={(date) => handleEndDateSelect(a.id, date)}
                        onClose={() => setShowEndCalendar(null)}
                        minDate={getMinEndDate(a.id)}
                      />
                    )}
                  </div>

                  <StatusDropdown
                    value={statuses[a.id] || a.type || a.status}
                    onChange={(status) => handleStatusChange(a.id, status)}
                    open={showStatusDropdown === a.id}
                    onToggle={() => {
                      setShowStatusDropdown(showStatusDropdown === a.id ? null : a.id);
                      setShowStartCalendar(null);
                      setShowEndCalendar(null);
                    }}
                    onClose={() => setShowStatusDropdown(null)}
                  />
                  <button 
                    className="btn small danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteActivity?.(a.id);
                    }}
                    type="button"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </section>
  );
}